/**
 * Cookie工具函数
 * 提供统一的获取和设置cookie的方法，避免undefined和null问题
 */

interface CookieOptions {
    maxAge?: number;
    path?: string;
    secure?: boolean;
}

/**
 * 获取cookie值
 * @param name cookie名称
 * @returns cookie值，如果不存在则返回空字符串
 */
export const getCookie = (name: string): string => {
    const cookieMatch = document.cookie.match(
        `(^|;)\\s*${name}\\s*=\\s*([^;]+)`,
    );
    const value = cookieMatch ? cookieMatch[2] : '';

    // 处理"undefined"或"null"字符串
    if (value === 'undefined' || value === 'null') {
        return '';
    }

    return value;
};

/**
 * 设置cookie值
 * @param name cookie名称
 * @param value cookie值
 * @param options cookie选项
 */
export const setCookie = (
    name: string,
    value: string | null | undefined,
    options: CookieOptions = {},
): void => {
    // 确保value是字符串，如果是undefined或null则设置为空字符串
    const cookieValue =
        value === undefined ||
        value === null ||
        value === 'undefined' ||
        value === 'null'
            ? ''
            : value;

    const {
        maxAge = 3600,
        path = '/',
        // secure设置为true要求只能通过 HTTPS 协议传输 Cookie
        secure = process.env.NODE_ENV === 'production',
    } = options;

    // 确保在客户端环境运行
    if (typeof window === 'undefined') {
        return;
    }

    const domain = window.location.hostname;
    const protocol = window.location.protocol;

    // 测试环境域名列表
    const testDomains = ['192.168.13.245', 'localhost', '127.0.0.1'];
    const isTestDomain = testDomains.includes(domain);

    // 测试环境且http协议，允许设置cookie
    const isLocalTestEnvironment = isTestDomain && protocol === 'http:';

    // 只在HTTPS协议下或者特定测试环境下设置cookie
    const isHttpsOrTestEnvironment =
        protocol === 'https:' || isLocalTestEnvironment;

    // 如果不是HTTPS协议且不是测试环境，则不设置cookie
    if (process.env.NODE_ENV === 'production' && !isHttpsOrTestEnvironment) {
        console.warn('Cookie只能在HTTPS协议下设置，当前协议：', protocol);
        return; // 直接返回，不执行cookie设置
    }

    // 开发环境下简化cookie设置，确保cookie能正常设置
    const cookieOptions = [
        `${name}=${cookieValue}`,
        `path=${path}`,
        `max-age=${maxAge}`,
        // 当域名不是192.168.13.245时，才包含这些属性
        ...(!isLocalTestEnvironment
            ? [
                  'SameSite=Strict', // 控制跨站请求时是否发送 Cookie, Strict：最严格，完全禁止跨站请求发送 Cookie
                  secure ? 'Secure' : '',
              ]
            : []),
    ]
        .filter(Boolean)
        .join('; ');

    document.cookie = cookieOptions;
};

/**
 * 删除cookie
 * @param name cookie名称
 */
export const removeCookie = (name: string): void => {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
};
